import { parseAssistantReply, parseCreateUuid, type ChatPoster } from '../../../commons/superconta-chat';
import { allEtiquetaTestsPassed } from './etiqueta-form';

export type EtiquetaScriptMessage = {
  level?: string;
  text?: string;
};

export type EtiquetaScriptTestResultItem = {
  testDescription?: string;
  passed?: boolean;
  description?: string;
};

export type EtiquetaScriptTestReport = {
  messages?: EtiquetaScriptMessage[];
  results?: EtiquetaScriptTestResultItem[];
  scriptValidated?: boolean;
};

export type EtiquetaScriptTester = (script: string, scriptTests: string) => Promise<EtiquetaScriptTestReport>;

export const SCRIPT_NO_GENERADO = 'Script no generado';

export function isEtiquetaGenerateError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : '';
  return message === SCRIPT_NO_GENERADO || message === 'La descripción es requerida para validar';
}

export function etiquetaScriptPrompt(descripcion: string): string {
  return `armame un script que ${descripcion.trim()}`;
}

function decodeHtmlEntities(value: string): string {
  return value
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&#x([0-9a-f]+);/gi, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_, dec) => String.fromCharCode(Number(dec)))
    .replace(/&amp;/g, '&');
}

function extractPreBlocks(html: string): string[] {
  const pattern = /<pre[^>]*>([\s\S]*?)<\/pre>/gi;
  const blocks: string[] = [];
  let match: RegExpExecArray | null = pattern.exec(html);
  while (match) {
    const inner = decodeHtmlEntities(match[1]).trim();
    if (inner) {
      blocks.push(inner);
    }
    match = pattern.exec(html);
  }
  return blocks;
}

export function extractScriptFromHtml(html: string): string {
  return extractPreBlocks(html)[0] ?? '';
}

export function extractScriptsFromHtml(html: string): { script: string; scriptTests: string } {
  const blocks = extractPreBlocks(html);
  return {
    script: blocks[0] ?? '',
    scriptTests: blocks[1] ?? '',
  };
}

async function askSuperContaHtml(poster: ChatPoster, content: string): Promise<string> {
  try {
    const uuid = parseCreateUuid(await poster('/api/chat/create'));
    const reply = parseAssistantReply(
      await poster('/api/chat/message', {
        uuid,
        messages: [{ role: 'user', content }],
      }),
    );
    return reply.html;
  } catch (err) {
    if (err instanceof Error && err.message === SCRIPT_NO_GENERADO) {
      throw err;
    }
    throw new Error(SCRIPT_NO_GENERADO);
  }
}

export async function completarScriptsEtiqueta(
  poster: ChatPoster,
  descripcion: string,
): Promise<{ script: string; scriptTests: string }> {
  const desc = descripcion.trim();
  if (!desc) {
    throw new Error('La descripción es requerida para validar');
  }
  const html = await askSuperContaHtml(poster, etiquetaScriptPrompt(desc));
  const scripts = extractScriptsFromHtml(html);
  if (!scripts.script || !scripts.scriptTests) {
    throw new Error(SCRIPT_NO_GENERADO);
  }
  return scripts;
}

export async function validarEtiquetaScripts(
  poster: ChatPoster,
  descripcion: string,
  tester: EtiquetaScriptTester,
  onScriptsReady?: (scripts: { script: string; scriptTests: string }) => void,
): Promise<{ script: string; scriptTests: string; report: EtiquetaScriptTestReport; scriptValido: boolean }> {
  const scripts = await completarScriptsEtiqueta(poster, descripcion);
  onScriptsReady?.(scripts);
  const report = await tester(scripts.script, scripts.scriptTests);
  return { ...scripts, report, scriptValido: allEtiquetaTestsPassed(report) };
}
